import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Banner, Button, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  associateFileWithWorkSecurityRole,
  previewAssociateFileWithWorkSecurityRole,
  AssociateFilePreview,
} from "services/work-security.service";
import { attachTrainingMaterial } from "services/work-security-training.service";
import DocumentTreeBrowser from "features/document-tree-picker/DocumentTreeBrowser";
import type { PickedDocument } from "features/document-tree-picker/DocumentTreeBrowser";
import {
  setDestroy,
  setIsModalxOpen,
  setModalxOptions,
} from "components/modalx/modalx.slice";

interface WorkSecuritySelectDocumentModalProps {
  /** Role attach mode (default). */
  roleId?: string;
  /** Training materials attach mode. */
  testId?: string;
  mode?: "role" | "training";
  /** @deprecated Prefer uploadCategoryIds */
  uploadCategoryId?: string;
  uploadCategoryIds?: string[];
  attachedWorkSecurityFileIds?: string[];
  /** File ids already attached as training materials (training mode). */
  attachedMaterialFileIds?: string[];
  onSuccess?: () => void;
}

function resolveUploadCategoryIds(
  uploadCategoryIds?: string[],
  uploadCategoryId?: string
): string[] {
  if (uploadCategoryIds?.length) {
    return uploadCategoryIds.filter(Boolean);
  }
  if (uploadCategoryId) {
    return [uploadCategoryId];
  }
  return [];
}

export default function WorkSecuritySelectDocumentModal({
  roleId,
  testId,
  mode = "role",
  uploadCategoryId,
  uploadCategoryIds,
  attachedWorkSecurityFileIds = [],
  attachedMaterialFileIds = [],
  onSuccess,
}: WorkSecuritySelectDocumentModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const isTrainingMode = mode === "training";
  const categoryIds = useMemo(
    () =>
      Array.from(
        new Set(resolveUploadCategoryIds(uploadCategoryIds, uploadCategoryId))
      ),
    [uploadCategoryIds, uploadCategoryId]
  );
  const categoryIdsKey = categoryIds.join(",");

  const [attachStep, setAttachStep] = useState<"select" | "confirm">("select");
  const [selected, setSelected] = useState<PickedDocument | null>(null);

  // Confirm (impact) step — role mode only
  const [error, setError] = useState<string | null>(null);
  const [associatePreview, setAssociatePreview] = useState<AssociateFilePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [associating, setAssociating] = useState(false);

  const excludeFileIds = isTrainingMode
    ? attachedMaterialFileIds
    : attachedWorkSecurityFileIds;
  const selectedFileId = selected?.id ?? "";

  const setModalTitle = useCallback(
    (step: "select" | "confirm") => {
      dispatch(
        setModalxOptions({
          title:
            step === "confirm"
              ? t("confirm_attach_document", { defaultValue: "Confirm attach document" })
              : t("select_existing_document", { defaultValue: "Select Document" }),
        })
      );
    },
    [dispatch, t]
  );

  useEffect(() => {
    setAttachStep("select");
    setSelected(null);
    setModalTitle("select");
  }, [setModalTitle, categoryIdsKey]);

  const handleFileRemove = () => {
    setSelected(null);
    setAssociatePreview(null);
  };

  const closeModal = () => {
    if (associating || previewLoading) return;
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleContinueToConfirm = async () => {
    if (!companyId || !roleId || !selectedFileId) return;
    try {
      setPreviewLoading(true);
      setError(null);
      const preview = await previewAssociateFileWithWorkSecurityRole(
        companyId,
        roleId,
        selectedFileId
      );
      setAssociatePreview(preview);
      setAttachStep("confirm");
      setModalTitle("confirm");
    } catch (err) {
      console.error("Failed to preview document association", err);
      const responseData = (err as { response?: { data?: { message?: string; code?: string } } })
        .response?.data;
      const message =
        responseData?.code === "WORK_SECURITY_FILE_ALREADY_ATTACHED"
          ? t("document_already_attached_to_role", {
              defaultValue:
                "This document is already attached to another role and cannot be attached again.",
            })
          : responseData?.message ||
            t("associate_preview_failed", {
              defaultValue: "Unable to load signature preview. Please try again.",
            });
      setError(message);
      setAttachStep("select");
      setModalTitle("select");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAttachTrainingMaterial = async () => {
    if (!companyId || !testId || !selectedFileId) return;
    try {
      setAssociating(true);
      setError(null);
      await attachTrainingMaterial(companyId, testId, selectedFileId);
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
      onSuccess?.();
    } catch (err) {
      console.error("Failed to attach training material", err);
      const responseData = (err as { response?: { data?: { message?: string } } })
        .response?.data;
      setError(
        responseData?.message ||
          t("attach_material_failed", {
            defaultValue: "Unable to attach the selected document. Please try again.",
          })
      );
    } finally {
      setAssociating(false);
    }
  };

  const handlePrimarySelectAction = () => {
    if (isTrainingMode) {
      void handleAttachTrainingMaterial();
      return;
    }
    void handleContinueToConfirm();
  };

  const handleBackToSelect = () => {
    if (associating || previewLoading) return;
    setAttachStep("select");
    setError(null);
    setModalTitle("select");
  };

  const handleAssociate = async () => {
    if (!companyId || !roleId || !selectedFileId) return;
    try {
      setAssociating(true);
      setError(null);
      await associateFileWithWorkSecurityRole(companyId, roleId, selectedFileId);
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
      onSuccess?.();
    } catch (err) {
      console.error("Failed to associate document with role", err);
      const responseData = (err as { response?: { data?: { message?: string; code?: string } } })
        .response?.data;
      const message =
        responseData?.code === "WORK_SECURITY_FILE_ALREADY_ATTACHED"
          ? t("document_already_attached_to_role", {
              defaultValue:
                "This document is already attached to another role and cannot be attached again.",
            })
          : responseData?.message ||
            t("associate_document_failed", {
              defaultValue: "Unable to attach the selected document. Please try again.",
            });
      setError(message);
      setAttachStep("select");
      setModalTitle("select");
    } finally {
      setAssociating(false);
    }
  };

  // The browser stays mounted (hidden) during the confirm step, so going back
  // keeps the folder, the search and the chosen file.
  return (
    <div className="space-y-4">
      <div className="space-y-3" hidden={attachStep !== "select"}>
        <p className="text-sm text-zinc-600">
          {isTrainingMode
            ? t("select_training_material_help", {
                defaultValue:
                  "Browse the upload category (from Settings) and its subfolders, or search across all of them. Documents already attached to this test are hidden.",
              })
            : t("select_existing_document_help", {
                defaultValue:
                  "Browse the upload category (from Settings) and its subfolders, or search across all of them. Documents already attached to a role are hidden.",
              })}
        </p>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {/* The browser reads an empty folder list as "the whole tree"; here that would
            ignore the upload folder configured in Settings, so it is an error instead. */}
        {companyId && categoryIds.length > 0 ? (
          <DocumentTreeBrowser
            companyId={companyId}
            rootCategoryIds={categoryIds}
            rootsTitle={t("upload_categories", { defaultValue: "Upload categories" })}
            excludeFileIds={excludeFileIds}
            selected={selected}
            onSelect={(document) => {
              setSelected(document);
              setError(null);
            }}
            onClear={handleFileRemove}
          />
        ) : (
          <Banner tone="critical">
            {t("selectable_documents_load_failed", {
              defaultValue: "Unable to load documents. Please try again.",
            })}
          </Banner>
        )}
      </div>

      {attachStep === "select" ? null : associatePreview ? (
        <div className="space-y-4 text-sm">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          <p className="text-zinc-700 dark:text-zinc-300">
            {t("confirm_attach_document_intro", {
              defaultValue:
                "Please review the signature impact for this file before attaching it to the role.",
            })}
          </p>

          <div>
            <p className="font-semibold text-zinc-900 dark:text-white">
              {t("file", { defaultValue: "File" })}
            </p>
            <p className="text-zinc-600 dark:text-zinc-400">{associatePreview.file.name}</p>
          </div>

          <div>
            <p className="font-semibold text-zinc-900 dark:text-white mb-1">
              {t("existing_signatures_on_file", {
                defaultValue: "Signatures already on this file",
              })}
            </p>
            {associatePreview.existingOnFile.length === 0 ? (
              <p className="text-zinc-500 italic">
                {t("no_existing_signatures_on_file", {
                  defaultValue: "No signatures on this file yet.",
                })}
              </p>
            ) : (
              <ul className="list-disc pl-5 space-y-1 text-zinc-700 dark:text-zinc-300">
                {associatePreview.existingOnFile.map((person) => (
                  <li key={`existing-${person.userId}`}>
                    {person.name}
                    {person.email ? ` (${person.email})` : ""}
                    {person.status ? ` — ${person.status}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="font-semibold text-zinc-900 dark:text-white mb-1">
              {t("signatures_will_be_added", {
                defaultValue: "Signatures that will be added",
              })}
            </p>
            {associatePreview.willAdd.length === 0 ? (
              <p className="text-zinc-500 italic">
                {associatePreview.assignedUsersCount === 0
                  ? t("no_users_assigned_to_role_for_signatures", {
                      defaultValue:
                        "No users are assigned to this role yet. Attach the document, then assign workers to send signature requests.",
                    })
                  : t("no_new_signatures_to_add", {
                      defaultValue:
                        "All assigned workers already have a signature on this file. No new invites will be sent.",
                    })}
              </p>
            ) : (
              <>
                <ul className="list-disc pl-5 space-y-1 text-zinc-700 dark:text-zinc-300">
                  {associatePreview.willAdd.map((person) => (
                    <li key={`add-${person.userId}`}>
                      {person.name}
                      {person.email ? ` (${person.email})` : ""}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-zinc-700 dark:text-zinc-300">
                  {t("signature_emails_will_be_sent", {
                    defaultValue:
                      "{{count}} signature request email(s) will be sent to these users.",
                    count: associatePreview.emailsWillBeSent,
                  })}
                </p>
              </>
            )}
          </div>

          {associatePreview.alreadyCovered.length > 0 && (
            <div>
              <p className="font-semibold text-zinc-900 dark:text-white mb-1">
                {t("already_have_signature_on_file", {
                  defaultValue: "Assigned workers already on this file (no new email)",
                })}
              </p>
              <ul className="list-disc pl-5 space-y-1 text-zinc-700 dark:text-zinc-300">
                {associatePreview.alreadyCovered.map((person) => (
                  <li key={`covered-${person.userId}`}>
                    {person.name}
                    {person.email ? ` (${person.email})` : ""}
                    {person.status ? ` — ${person.status}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Banner tone="info">
            {t("existing_signatures_kept_notice", {
              defaultValue:
                "Existing signatures on this file will be kept. New signature requests are only added for assigned workers who are not already on the file.",
            })}
          </Banner>
        </div>
      ) : (
        <div className="flex justify-center py-8">
          <Spinner size="large" />
        </div>
      )}

      <div className="flex justify-end gap-3 mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
        {attachStep === "confirm" ? (
          <>
            <Button onClick={handleBackToSelect} disabled={associating}>
              {t("back", { defaultValue: "Back" })}
            </Button>
            <Button onClick={closeModal} disabled={associating}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="primary"
              onClick={handleAssociate}
              loading={associating}
              disabled={associating || !associatePreview}
            >
              {t("attach_and_send_requests", {
                defaultValue: "Attach & send signature requests",
              })}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={closeModal} disabled={previewLoading || associating}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="primary"
              onClick={handlePrimarySelectAction}
              loading={isTrainingMode ? associating : previewLoading}
              disabled={
                !selectedFileId ||
                previewLoading ||
                associating ||
                (isTrainingMode ? !testId : !roleId)
              }
            >
              {isTrainingMode
                ? t("attach", { defaultValue: "Attach" })
                : t("continue", { defaultValue: "Continue" })}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}